import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface AccreditationBadgesProps {
  accreditations: string[];
  maxDisplay?: number;
  size?: "sm" | "md" | "lg";
}

// Common real estate accreditations with their full names
const ACCREDITATION_NAMES: Record<string, string> = {
  "CRS": "Certified Residential Specialist",
  "ABR": "Accredited Buyer's Representative",
  "GRI": "Graduate, REALTOR® Institute",
  "SRES": "Seniors Real Estate Specialist",
  "SRS": "Seller Representative Specialist",
  "CIPS": "Certified International Property Specialist",
  "CNE": "Certified Negotiation Expert",
  "ePRO": "e-PRO Certified",
  "GREEN": "NAR Green Designation",
  "MRP": "Military Relocation Professional",
  "PSA": "Pricing Strategy Advisor",
  "RENE": "Real Estate Negotiation Expert",
  "RSPS": "Resort and Second-Home Property Specialist",
  "AHWD": "At Home With Diversity",
  "C2EX": "Commitment to Excellence",
  "BPOR": "Broker Price Opinion Resource",
  "CCIM": "Certified Commercial Investment Member",
  "CPM": "Certified Property Manager",
  "REALTOR": "REALTOR®",
};

export const AccreditationBadges = ({ 
  accreditations, 
  maxDisplay = 4,
  size = "sm" 
}: AccreditationBadgesProps) => {
  if (!accreditations || accreditations.length === 0) return null;

  const displayedAccreditations = accreditations.slice(0, maxDisplay);
  const remainingCount = accreditations.length - maxDisplay;

  const sizeClasses = {
    sm: "text-xs px-2 py-0.5",
    md: "text-sm px-2.5 py-1",
    lg: "text-base px-3 py-1.5",
  };

  return (
    <div className="flex flex-wrap gap-1.5">
      {displayedAccreditations.map((accreditation, index) => {
        const fullName = ACCREDITATION_NAMES[accreditation.toUpperCase()];
        
        return fullName ? (
          <Tooltip key={index}>
            <TooltipTrigger asChild>
              <Badge 
                variant="outline" 
                className={`${sizeClasses[size]} border-primary/30 text-primary hover:bg-primary/10 cursor-help`}
              >
                {accreditation}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <p>{fullName}</p>
            </TooltipContent>
          </Tooltip>
        ) : (
          <Badge 
            key={index}
            variant="outline" 
            className={`${sizeClasses[size]} border-primary/30 text-primary`}
          >
            {accreditation}
          </Badge>
        );
      })}
      
      {remainingCount > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge 
              variant="secondary" 
              className={`${sizeClasses[size]} cursor-help`}
            >
              +{remainingCount} more
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <p>{accreditations.slice(maxDisplay).join(", ")}</p>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};

export default AccreditationBadges;
